import { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

const VEHICLE_ICONS = ['🚕','🚗','🚙','🚐','🚌','🚚','🚛','🏗️','🚜','🛻','🚑','🚒','🏎️','🏍️','🛵','🚲','🛺','🚤','🛥️','⛵','🚁','🏕️','🎿','🏄','🛶'];
const INS_ICONS     = ['🛡️','💎','⭐','🏆','🎯','🌟','✨','🔐','🔵','🌈','🚘','💼'];
const INS_COLORS    = ['#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#1e40af','#065f46'];

const BLANK_PLAN = {
  id: '', icon: '🛡️', name: '', color: '#0ea5e9',
  price: '', excess: '', deposit: '', desc: '', recommended: false,
};

const DEFAULT_FORM = {
  modelId: '',
  icon: '🚗', name: '', plate: '', type: 'Car', seats: '', features: '',
  transmission: 'automatic', mileage: 'unlimited', fuelType: 'petrol',
  priceDay: '', confirm: 'manual', odometer: '',
  bookPage: true, groupId: '', useGroupPricing: false, agreementId: '',
  insuranceMode: 'standard', customInsurance: [],
  photos: [],
  currentAreaId: '', dropoffAreaIds: 'all',
};

const MAX_PHOTOS = 5;

// ── Plan editor — defined OUTSIDE main component to prevent focus loss ────────
function PlanForm({ planForm, setPlanForm, onSave, onCancel }) {
  function PF(k) {
    return { value: planForm[k] ?? '', onChange: e => setPlanForm(p => ({ ...p, [k]: e.target.value })) };
  }
  return (
    <div style={{ border: '2px solid var(--ac)', borderRadius: 'var(--r)', padding: '12px 14px', background: 'var(--ac-l)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="fg" style={{ margin: 0 }}>
        <label className="fl">Icon</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {INS_ICONS.map(ic => (
            <div key={ic} onClick={() => setPlanForm(p => ({ ...p, icon: ic }))}
              style={{ fontSize: 18, padding: '3px 7px', borderRadius: 6, cursor: 'pointer',
                border: `2px solid ${planForm.icon === ic ? 'var(--ac)' : 'var(--bd)'}`,
                background: planForm.icon === ic ? 'var(--ac-l)' : 'var(--sur)' }}>
              {ic}
            </div>
          ))}
        </div>
      </div>

      <div className="fg" style={{ margin: 0 }}>
        <label className="fl">Colour</label>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {INS_COLORS.map(c => (
            <div key={c} onClick={() => setPlanForm(p => ({ ...p, color: c }))}
              style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                outline: planForm.color === c ? `3px solid ${c}` : '2px solid transparent',
                outlineOffset: 2, transition: 'outline .1s' }} />
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="fg" style={{ gridColumn: '1/-1', margin: 0 }}>
          <label className="fl">Plan name</label>
          <input type="text" className="fi" placeholder="e.g. Luxury Full Coverage" {...PF('name')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Price (€/day)</label>
          <input type="number" className="fi" placeholder="0 = Included" {...PF('price')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Excess</label>
          <input type="text" className="fi" placeholder="e.g. €500 or Nil" {...PF('excess')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Deposit (€)</label>
          <input type="number" className="fi" placeholder="0" {...PF('deposit')} />
        </div>
        <div className="fg" style={{ margin: 0, gridColumn: '1/-1' }}>
          <label className="fl">Description</label>
          <input type="text" className="fi" placeholder="e.g. Full cover, zero excess, unlimited mileage" {...PF('desc')} />
        </div>
      </div>

      <div onClick={() => setPlanForm(p => ({ ...p, recommended: !p.recommended }))}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 11 }}>
        <div style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0,
          border: `2px solid ${planForm.recommended ? 'var(--gr)' : 'var(--bd)'}`,
          background: planForm.recommended ? 'var(--gr)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700,
        }}>{planForm.recommended ? '✓' : ''}</div>
        Mark as recommended plan
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
        <button className="btn btn-primary btn-sm" onClick={onSave}>Save Plan</button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function AddVehicleModal() {
  const { modals, closeModal, addVehicle, updateVehicle, vehicles, groups, vehicleTypes, vehicleModels, areas, showToast, agreementTemplates } = useApp();
  const open     = modals['add-vehicle'];
  const vid      = modals['add-vehicle-vid'];
  const isEdit   = !!(vid && typeof vid === 'string');
  const existing = isEdit ? vehicles.find(v => v.id === vid) : null;

  const [form,     setForm]     = useState(DEFAULT_FORM);
  const [planEdit, setPlanEdit] = useState(null);   // null | 'new' | number
  const [planForm, setPlanForm] = useState({ ...BLANK_PLAN });
  const photoInputRef = useRef(null);
  const [photoSlotIdx, setPhotoSlotIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        modelId:         existing.modelId         || '',
        icon:            existing.icon            || '🚗',
        name:            existing.name            || '',
        plate:           existing.plate           || '',
        type:            existing.type            || 'Car',
        seats:           existing.seats           || '',
        features:        existing.features        || '',
        transmission:    existing.transmission    || 'automatic',
        mileage:         existing.mileage         || 'unlimited',
        fuelType:        existing.fuelType        || 'petrol',
        priceDay:        existing.priceDay        ?? '',
        confirm:         existing.confirm         || 'manual',
        odometer:        existing.odometer        ?? '',
        bookPage:        existing.bookPage        ?? true,
        groupId:         existing.groupId         || '',
        useGroupPricing: existing.useGroupPricing || false,
        insuranceMode:   existing.insuranceMode   || 'standard',
        customInsurance: existing.customInsurance || [],
        photos:          existing.photos          || [],
        currentAreaId:   existing.currentAreaId   || '',
        dropoffAreaIds:  existing.dropoffAreaIds  ?? 'all',
        agreementId:     existing.agreementId     || '',
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setPlanEdit(null);
  }, [open, vid]);

  function F(k) { return { value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) }; }

  // ── Plan helpers ──────────────────────────────────────────────────────────
  function openNewPlan()   { setPlanForm({ ...BLANK_PLAN, id: 'ci-' + Date.now() }); setPlanEdit('new'); }
  function openEditPlan(i) { setPlanForm({ ...form.customInsurance[i] }); setPlanEdit(i); }
  function savePlan() {
    if (!planForm.name.trim()) { showToast('Plan name is required'); return; }
    const plans = [...form.customInsurance];
    if (planEdit === 'new') plans.push({ ...planForm });
    else plans[planEdit] = { ...planForm };
    setForm(p => ({ ...p, customInsurance: plans }));
    setPlanEdit(null);
  }
  function deletePlan(i) {
    setForm(p => ({ ...p, customInsurance: p.customInsurance.filter((_, j) => j !== i) }));
    if (planEdit === i) setPlanEdit(null);
  }
  // ─────────────────────────────────────────────────────────────────────────

  // ── Photo helpers ─────────────────────────────────────────────────────────
  function openPhotoSlot(idx) { setPhotoSlotIdx(idx); photoInputRef.current?.click(); }
  function onPhotoFile(e) {
    const file = e.target.files?.[0];
    if (!file || photoSlotIdx === null) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(p => {
      const photos = [...(p.photos || [])];
      photos[photoSlotIdx] = ev.target.result;
      return { ...p, photos };
    });
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function removePhoto(idx) {
    setForm(p => {
      const photos = [...(p.photos || [])];
      photos.splice(idx, 1);
      return { ...p, photos };
    });
  }
  // ─────────────────────────────────────────────────────────────────────────

  function submit() {
    if (!form.name || !form.plate) { showToast('Name and plate are required'); return; }
    const parsed = {
      ...form,
      modelId:   form.modelId || null,
      priceDay:  parseFloat(form.priceDay) || null,
      odometer:  parseInt(form.odometer) || 0,
      groupId:   form.groupId || null,
      useGroupPricing: form.groupId ? form.useGroupPricing : false,
      customInsurance: form.insuranceMode === 'custom' ? form.customInsurance : [],
      currentAreaId:   form.currentAreaId || null,
      dropoffAreaIds:  form.dropoffAreaIds,
    };
    if (isEdit) {
      updateVehicle(vid, parsed);
      showToast(`✓ ${form.name} updated`);
    } else {
      const id = form.plate.toLowerCase().replace(/[^a-z0-9]/g, '');
      addVehicle({ ...parsed, id, status: 'available' });
      showToast(`✓ ${form.name} added to fleet`);
    }
    closeModal('add-vehicle');
  }

  const selectedGroup = form.groupId ? groups.find(g => g.id === form.groupId) : null;

  if (!open) return null;

  return (
    <Modal open={!!open} onClose={() => closeModal('add-vehicle')}>
      <ModalHeader
        title={isEdit ? 'Edit Vehicle' : 'Add Vehicle'}
        subtitle={isEdit ? `Editing ${existing?.name || ''}` : 'Add a new vehicle or machine to your fleet'}
        onClose={() => closeModal('add-vehicle')}
      />
      <ModalBody>
        {(() => {
          const hasModel = !!form.modelId;
          const selectedModel = hasModel ? vehicleModels.find(m => m.id === form.modelId) : null;
          return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* ── MODEL PICKER ─────────────────────────────────────────── */}
          <div style={{ background: hasModel ? 'var(--ac-l)' : 'var(--sur2)', border: `1px solid ${hasModel ? 'var(--ac)' : 'var(--bd)'}`, borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">🏷️ Model</label>
              <select
                className="fsel2"
                value={form.modelId}
                onChange={e => setForm(p => ({ ...p, modelId: e.target.value }))}
              >
                <option value="">— No model (individual vehicle)</option>
                {(vehicleModels || []).map(m => (
                  <option key={m.id} value={m.id}>{m.icon} {m.name} — €{m.priceDay}/day</option>
                ))}
              </select>
            </div>
            {hasModel && selectedModel && (
              <div style={{ fontSize: 11, color: 'var(--tx2)', lineHeight: 1.6 }}>
                ✅ This vehicle inherits from <strong>{selectedModel.name}</strong>:
                photos, features, transmission, fuel, seats, mileage, price, deposit, contract, group, confirmation.
                <br />
                Only <strong>plate, status, odometer, MOT and location</strong> are vehicle-specific.
                <br />
                <span style={{ color: 'var(--tx3)' }}>To change inherited values, edit the model in Fleet &amp; Pricing → Models.</span>
              </div>
            )}
            {!hasModel && (
              <div style={{ fontSize: 11, color: 'var(--tx3)', lineHeight: 1.6 }}>
                💡 Tip: Pick a model to inherit photos &amp; specs (recommended when you have multiple identical cars).
                Leave empty to manage this vehicle individually.
              </div>
            )}
          </div>

          {/* Icon selector — hidden when model assigned */}
          {!hasModel && (
          <div className="fg">
            <label className="fl">Icon</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {VEHICLE_ICONS.map(ic => (
                <div key={ic} onClick={() => setForm(p => ({ ...p, icon: ic }))}
                  style={{ fontSize: 20, padding: '4px 8px', borderRadius: 7, cursor: 'pointer',
                    border: `2px solid ${form.icon === ic ? 'var(--ac)' : 'var(--bd)'}`,
                    background: form.icon === ic ? 'var(--ac-l)' : 'var(--sur)' }}>
                  {ic}
                </div>
              ))}
            </div>
          </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="fg">
              <label className="fl">Vehicle name</label>
              <input type="text" className="fi" placeholder={hasModel ? (selectedModel?.name || '') : 'e.g. TAXI – MZZ099'} {...F('name')} />
            </div>
            <div className="fg">
              <label className="fl">Plate number</label>
              <input type="text" className="fi" placeholder="e.g. MZZ099" {...F('plate')}
                disabled={isEdit} style={isEdit ? { opacity: 0.6 } : {}} />
            </div>
            {!hasModel && (<>
            <div className="fg">
              <label className="fl">Type</label>
              <select className="fsel2" {...F('type')}>
                {vehicleTypes.map(t => (
                  <option key={t.id} value={t.name}>{t.icon} {t.name}</option>
                ))}
              </select>
            </div>
            <div className="fg">
              <label className="fl">Seats / Capacity</label>
              <input type="number" className="fi" placeholder="e.g. 5" min="1" {...F('seats')} />
            </div>
            <div className="fg">
              <label className="fl">⚙️ Transmission</label>
              <select className="fsel2" {...F('transmission')}>
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl">♾️ Mileage</label>
              <select className="fsel2" {...F('mileage')}>
                <option value="unlimited">Unlimited</option>
                <option value="limited">Limited</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl">⛽ Fuel Type</label>
              <select className="fsel2" {...F('fuelType')}>
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            </>)}
            <div className="fg">
              <label className="fl">🔢 Odometer (km)</label>
              <input type="number" className="fi" placeholder="e.g. 124500" min="0" {...F('odometer')} />
            </div>
            {!hasModel && (
            <div className="fg">
              <label className="fl">Extra Features</label>
              <input type="text" className="fi" placeholder="e.g. A/C, GPS, Roof rack" {...F('features')} />
            </div>
            )}
          </div>

          {/* Agreement override — hidden when model assigned */}
          {!hasModel && agreementTemplates.length > 0 && (
            <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>📋 Agreement</div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Agreement (overrides group)</label>
                <select className="fsel2" value={form.agreementId} onChange={e => setForm(p => ({ ...p, agreementId: e.target.value }))}>
                  <option value="">Use group default</option>
                  {agreementTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Group assignment — hidden when model assigned (model holds the group) */}
          {!hasModel && groups.length > 0 && (
            <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>Group & Pricing</div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Assign to Group</label>
                <select className="fsel2" value={form.groupId} onChange={e => setForm(p => ({ ...p, groupId: e.target.value, useGroupPricing: false }))}>
                  <option value="">— No group (individual)</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.icon} {g.name} — €{g.priceDay}/day</option>)}
                </select>
              </div>
              {form.groupId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setForm(p => ({ ...p, useGroupPricing: !p.useGroupPricing }))}>
                  <div style={{ width: 34, height: 18, borderRadius: 10, background: form.useGroupPricing ? 'var(--gr)' : 'var(--bd)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 2, left: form.useGroupPricing ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--tx2)' }}>
                    Use group price (€{selectedGroup?.priceDay}/day from {selectedGroup?.name})
                    {!form.useGroupPricing && ' — currently using individual price below'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Area Assignment ─────────────────────────────────────── */}
          {areas.length > 0 && (
            <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>📍 Area Assignment</div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Current Area</label>
                <select className="fsel2" value={form.currentAreaId} onChange={e => setForm(p => ({ ...p, currentAreaId: e.target.value }))}>
                  <option value="">— Not set</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.icon} {a.name}</option>)}
                </select>
              </div>
              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Drop-off Areas</label>
                <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
                  {[
                    { val: 'all',      label: 'All areas' },
                    { val: 'specific', label: 'Specific areas' },
                  ].map(opt => (
                    <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer' }}>
                      <input type="radio" name="dropoffMode" checked={form.dropoffAreaIds === 'all' ? opt.val === 'all' : opt.val === 'specific'}
                        onChange={() => setForm(p => ({ ...p, dropoffAreaIds: opt.val === 'all' ? 'all' : [] }))} />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {Array.isArray(form.dropoffAreaIds) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 4 }}>
                    {areas.map(a => {
                      const checked = form.dropoffAreaIds.includes(a.id);
                      return (
                        <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer' }}>
                          <input type="checkbox" checked={checked}
                            onChange={() => setForm(p => ({
                              ...p,
                              dropoffAreaIds: checked
                                ? p.dropoffAreaIds.filter(id => id !== a.id)
                                : [...p.dropoffAreaIds, a.id],
                            }))} />
                          {a.icon} {a.name}
                        </label>
                      );
                    })}
                    {form.dropoffAreaIds.length === 0 && (
                      <div style={{ fontSize: 10, color: 'var(--tx3)', fontStyle: 'italic' }}>Select at least one area</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual Pricing — hidden when model assigned */}
          {!hasModel && (<>
          <div style={{ fontWeight: 600, fontSize: 11, marginTop: 4, color: form.useGroupPricing ? 'var(--tx3)' : 'var(--tx)' }}>
            Individual Pricing {form.useGroupPricing && <span style={{ fontWeight: 400, fontSize: 10 }}>(overridden by group rate)</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, opacity: form.useGroupPricing ? 0.45 : 1 }}>
            <div className="fg">
              <label className="fl">Day rate (€)</label>
              <input type="number" className="fi" placeholder="0" disabled={form.useGroupPricing} {...F('priceDay')} />
            </div>
            <div className="fg">
              <label className="fl">Confirmation</label>
              <select className="fsel2" {...F('confirm')}>
                <option value="manual">Manual</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
          </>)}

          {/* Confirmation is inherited from the model — no per-vehicle override */}

          {/* ── Insurance Plans — hidden when model assigned ──────────────── */}
          {!hasModel && (
          <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>🛡️ Insurance Plans</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                { val: 'standard', label: '📋 Standard Plans',         desc: 'Show your global insurance options for this vehicle' },
                { val: 'custom',   label: '✏️ Custom for this vehicle', desc: 'Vehicle-specific plans with custom deposit & excess' },
              ].map(opt => (
                <div key={opt.val} onClick={() => setForm(p => ({ ...p, insuranceMode: opt.val }))} style={{
                  border: `2px solid ${form.insuranceMode === opt.val ? 'var(--ac)' : 'var(--bd)'}`,
                  borderRadius: 'var(--r)', padding: '8px 10px', cursor: 'pointer',
                  background: form.insuranceMode === opt.val ? 'var(--ac-l)' : 'var(--sur)',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 2 }}>{opt.desc}</div>
                </div>
              ))}
            </div>

            {form.insuranceMode === 'custom' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

                {form.customInsurance.map((plan, idx) => (
                  planEdit === idx ? (
                    <PlanForm key={idx} planForm={planForm} setPlanForm={setPlanForm} onSave={savePlan} onCancel={() => setPlanEdit(null)} />
                  ) : (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: 'var(--sur2)', border: `1.5px solid ${plan.color || 'var(--bd)'}`, borderRadius: 'var(--r)' }}>
                      <span style={{ fontSize: 22 }}>{plan.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: plan.color }}>{plan.name}</span>
                          {plan.recommended && (
                            <span style={{ fontSize: 8, background: plan.color, color: '#fff', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>Recommended</span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>
                          {plan.price > 0 ? `€${plan.price}/day` : 'Included'} · Excess: {plan.excess || '—'} · Deposit: €{plan.deposit || 0}
                        </div>
                        {plan.desc && <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 1 }}>{plan.desc}</div>}
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEditPlan(idx)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)' }} onClick={() => deletePlan(idx)}>✕</button>
                    </div>
                  )
                ))}

                {planEdit === 'new'
                  ? <PlanForm planForm={planForm} setPlanForm={setPlanForm} onSave={savePlan} onCancel={() => setPlanEdit(null)} />
                  : <button className="btn btn-ghost btn-sm" onClick={openNewPlan} style={{ alignSelf: 'flex-start', marginTop: 2 }}>+ Add Insurance Plan</button>
                }

                {form.customInsurance.length === 0 && planEdit !== 'new' && (
                  <div style={{ fontSize: 10, color: 'var(--tx3)', fontStyle: 'italic' }}>
                    No plans added yet — standard plans will show until you add at least one custom plan.
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* ── Vehicle Photos — hidden when model assigned (photos live on model) ─ */}
          {!hasModel && (
          <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', marginBottom: 10 }}>
              📷 Vehicle Photos
              <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>
                {(form.photos || []).filter(Boolean).length}/{MAX_PHOTOS} added · shown on booking page
              </span>
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoFile} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {Array.from({ length: MAX_PHOTOS }).map((_, idx) => {
                const src = (form.photos || [])[idx];
                return (
                  <div key={idx} style={{
                    border: `2px ${src ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                    borderRadius: 8, overflow: 'hidden',
                    background: src ? 'transparent' : 'var(--sur2)',
                    aspectRatio: '4/3',
                  }}>
                    {src ? (
                      <div style={{ position: 'relative', height: '100%' }}>
                        <img src={src} alt={`Photo ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => window.open(src, '_blank')} />
                        <button
                          onClick={() => removePhoto(idx)}
                          style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                          ✕
                        </button>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: 8, background: 'rgba(0,0,0,.4)', color: '#fff', padding: '2px 0' }}>
                          {idx === 0 ? 'Cover' : `#${idx + 1}`}
                        </div>
                      </div>
                    ) : (
                      <div onClick={() => openPhotoSlot(idx)}
                        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: 3, padding: 4 }}>
                        <div style={{ fontSize: 16 }}>📷</div>
                        <div style={{ fontSize: 8, color: 'var(--tx3)', textAlign: 'center' }}>{idx === 0 ? 'Cover' : `Photo ${idx + 1}`}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Show on booking page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--bd)', borderRadius: 'var(--r)', cursor: 'pointer' }}
            onClick={() => setForm(p => ({ ...p, bookPage: !p.bookPage }))}>
            <div style={{ width: 18, height: 18, borderRadius: 4,
              border: `2px solid ${form.bookPage ? 'var(--gr)' : 'var(--bd)'}`,
              background: form.bookPage ? 'var(--gr)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700,
            }}>{form.bookPage ? '✓' : ''}</div>
            <span style={{ fontSize: 11 }}>Show on booking page</span>
          </div>

        </div>
          );
        })()}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('add-vehicle')}>Cancel</button>
        <button className="btn btn-primary" onClick={submit}>{isEdit ? 'Save Changes' : 'Add Vehicle'}</button>
      </ModalFooter>
    </Modal>
  );
}
